import React from "react"
import classNames from "classnames"
import { FixedSizeList as WindowList } from "react-window"
import AutoSizer from "react-virtualized-auto-sizer"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileLines } from "@fortawesome/free-regular-svg-icons"
import * as DateFns from "date-fns"

import { Idea, SourceStatus, WebSource } from "../../../../../model/types"
import { useNana } from "../../../../hooks/useNana"
import IdeaBox, { GhostBox } from "../../../../components/idea_box"

// FixedSizeList needs one height per tab. Inner box is 1px shorter so the
// row border sits inside the slot (same pattern as before summaries).
const NEW_ITEM_SIZE = 137
const OTHER_ITEM_SIZE = 176
const NEW_ITEM_HEIGHT = NEW_ITEM_SIZE - 1
const OTHER_ITEM_HEIGHT = OTHER_ITEM_SIZE

const deschemeUrl = (url: string) => {
  return url.replace(/https?:\/\//, "")
}

const ListItem = ({
  source,
  isOpen,
  style,
  setOpenSource,
}: {
  source: WebSource
  isOpen: boolean
  style: React.CSSProperties
  setOpenSource: (source: WebSource) => void
}) => {
  const { model } = useNana()
  const goal: Idea | null = source.goalId ? model.getIdea(source.goalId) : null

  const attachedIdeas = model.getIdeasFromSource(source.id)
  const attachedIdeaCount =
    model.countIdeasWithSource(source.id) +
    model.countFleetingIdeasWithSource(source.id)

  return (
    <li key={source.id} onClick={() => setOpenSource(source)} style={style}>
      <div
        className={classNames(
          "overflow-hidden px-4 py-4 flex flex-row gap-4 justify-between border-b  hover:bg-zinc-100 dark:border-zinc-800  dark:hover:border-zinc-700 dark:hover:bg-zinc-800 cursor-pointer",
          {
            "bg-white dark:bg-gray-900": !isOpen,
            "bg-sky-50 dark:bg-sky-950 hover:bg-sky-100": isOpen,
            // "h-36": source.status == "read",
            // "h-28": source.status != "read",
          }
        )}
        style={{
          height: source.status == "new" ? NEW_ITEM_HEIGHT : OTHER_ITEM_HEIGHT,
        }}
      >
        <div className="flex flex-col gap-2 items-center justify-between ">
          {/* <SourceStatusTag source={source} /> */}
          {goal && <IdeaBox idea={goal} />}
          {!goal && <GhostBox type={"goal"} />}
          <div className="mt-2 flex gap-2 items-center">
            {attachedIdeaCount > 0 && (
              <div className=" text-gray-500">
                <div className="">{attachedIdeaCount}</div>
              </div>
            )}
            {source.literatureNotes != null &&
              source.literatureNotes != "" &&
              source.literatureNotes != "<p></p>" && (
                <FontAwesomeIcon icon={faFileLines} className="text-gray-400" />
              )}
          </div>
        </div>

        <div className="h-full min-w-0 flex grow flex-col justify-between items-start">
          <div className="min-h-0 w-full grow flex flex-col justify-between items-stretch">
            <div>
              <div
                className={classNames(
                  "text-sm font-medium line-clamp-2 overflow-hidden text-ellipsis",
                  {
                    "break-all": !source.name,
                    "break-words": !!source.name,
                  }
                )}
              >
                {source.name || (source.url ? deschemeUrl(source.url) : "Source")}
              </div>
              <div
                className="mt-1 min-h-[2.0625rem] text-xs leading-snug text-gray-500 line-clamp-2 overflow-hidden"
                data-testid="source-list-summary"
              >
                {source.summary}
              </div>
            </div>
            <div className="mt-2 w-full flex justify-between items-center gap-2 text-sm text-gray-500 ">
              <div className="line-clamp-1 overflow-hidden text-ellipsis min-w-0">
                {source.author}
              </div>
              <div className="line-clamp-1 shrink-0 flex items-center gap-1.5">
                {source.type ? (
                  <span data-testid="source-list-type">{source.type}</span>
                ) : null}
                {source.type && source.publicationDate ? (
                  <span aria-hidden="true">·</span>
                ) : null}
                {source.publicationDate
                  ? DateFns.format(source.publicationDate, "MMM d, yyyy")
                  : null}
              </div>
            </div>
          </div>
          {(source.status == "queued" || source.status == "read") && (
            <div
              className={classNames("mt-4 flex gap-1 h-8 xoverflow-x-hidden", {
                "opacity-50": !isOpen,
              })}
            >
              {attachedIdeas.map((idea) => (
                <IdeaBox key={idea.id} idea={idea} shadow={false} />
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

const ListState: { new: number; queued: number; read: number } = {
  new: 0,
  queued: 0,
  read: 0,
}

const List = ({
  sources,
  openSource,
  setOpenSource,
  sourceStatus,
}: {
  sources: WebSource[]
  openSource: WebSource | null
  setOpenSource: (source: WebSource) => void
  sourceStatus: SourceStatus
}) => {
  const { model } = useNana()
  return (
    <div
      id="sources"
      className="overflow-y-auto space-y-3"
      style={{ flex: "1 1 auto" }}
      data-testid={"sources-list-" + sourceStatus}
    >
      <AutoSizer>
        {({ height, width }) => (
          <WindowList
            innerElementType={"ul"}
            height={height}
            itemCount={sources.length}
            itemSize={sourceStatus == "new" ? NEW_ITEM_SIZE : OTHER_ITEM_SIZE}
            width={width}
            onScroll={({ scrollOffset }) =>
              (ListState[sourceStatus] = scrollOffset)
            }
            initialScrollOffset={ListState[sourceStatus]}
          >
            {({ index, style }) => {
              const source = sources[index]
              return (
                <ListItem
                  key={source.id}
                  style={style}
                  source={source}
                  isOpen={!!openSource && source.id === openSource.id}
                  setOpenSource={setOpenSource}
                />
              )
            }}
          </WindowList>
        )}
      </AutoSizer>
    </div>
  )
}

export default List
